'use client';

import { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle, Loader2, Mail, Lock, Stethoscope, Heart } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from 'react-i18next';

export default function LoginPage() {
  const { t } = useTranslation('doctor-platform');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { 
    user, 
    profile,
    loading, 
    isSigningIn, 
    login,
    error: authError 
  } = useAuth();

  // Pre-fill email from location state if available
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  // Redirect if already logged in
  useEffect(() => {
    if (!loading && user) {
      if (user.role === 'doctor' || user.role === 'doctor_pending') {
        const from = location.state?.from || '/dashboard';
        navigate(from, { replace: true });
      }
    }
  }, [user, loading, location.state, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError(t('auth.login.validation.missingCredentials'));
      return;
    }

    try {
      const result = await login(email, password);
      
      if (!result.success) {
        setError(result.error || t('auth.login.validation.loginFailed'));
        return;
      }

      // Success - show toast and navigate
      toast({
        title: t('auth.login.toast.title'),
        description: t('auth.login.toast.description'),
      });

      // Navigate to dashboard or the intended destination
      const from = location.state?.from || '/dashboard';
      navigate(from, { replace: true });

    } catch (err: any) {
      setError(t('auth.login.validation.genericError'));
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center p-8 sm:p-12 lg:p-16 bg-white dark:bg-gray-900">
        <div className="mx-auto w-full max-w-md space-y-8">
          {/* Logo and Title */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
                <Stethoscope className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
              {t('auth.login.title')}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('auth.login.subtitle')}
            </p>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">{t('auth.login.emailLabel')}</Label>
              <div className="mt-1 relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder={t('auth.login.emailPlaceholder')}
                  className="pl-10"
                  disabled={isSigningIn}
                />
                <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div>
              <Label htmlFor="password">{t('auth.login.passwordLabel')}</Label>
              <div className="mt-1 relative">
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder={t('auth.login.passwordPlaceholder')}
                  className="pl-10"
                  disabled={isSigningIn}
                />
                <Lock className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Link
                to="/auth/forgot-password"
                className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400"
              >
                {t('auth.login.forgotPassword')}
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isSigningIn}
            >
              {isSigningIn ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth.login.submitting')}
                </>
              ) : (
                t('auth.login.submit')
              )}
            </Button>

            <div className="text-center">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('auth.login.signupPrompt')}{' '}
              </span>
              <Link
                to="/auth/signup"
                className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400"
              >
                {t('auth.login.signup')}
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?q=80&w=2070"
          alt={t('auth.login.imageAlt')}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/20" />
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <div className="flex items-center mb-4">
            <Heart className="h-8 w-8 mr-3 text-red-400" />
            <h3 className="text-2xl font-bold">{t('auth.login.welcomeTitle')}</h3>
          </div>
          <p className="text-lg opacity-90">
            {t('auth.login.welcomeBody')}
          </p>
        </div>
      </div>
    </div>
  );
}
